"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";

/** Typography + scale aligned with home `Hero` “Popular Searches” pills (`hero.tsx`). */
const heroStyleSectorPillClass =
	"inline-flex h-[24px] max-w-full shrink-0 snap-start items-center justify-center text-nowrap rounded-full bg-secondary/5 px-3 text-[10px] font-normal leading-none text-secondary xs:px-3.5 xs:text-[11px] sm:h-[28px] sm:px-6 sm:text-xsm2 md:px-4 md:text-xs";

type Props = {
	labels: string[];
	/**
	 * When true and there are more than four labels, clip the strip to ~four pills wide,
	 * fade the right edge, and allow horizontal scroll for the rest.
	 */
	constrainedScroll: boolean;
};

export default function CategoryBannerSectorPillStrip({ labels, constrainedScroll }: Props) {
	const scrollRef = useRef<HTMLUListElement>(null);
	const [maxPx, setMaxPx] = useState<number | null>(null);
	const [fadeEnd, setFadeEnd] = useState(false);

	const scrollMode = constrainedScroll && labels.length > 4;

	const measure = useCallback(() => {
		const ul = scrollRef.current;
		if (!ul || !scrollMode || labels.length < 5) {
			setMaxPx(null);
			return;
		}
		const items = ul.querySelectorAll(":scope > li");
		if (items.length < 5) {
			setMaxPx(null);
			return;
		}
		let total = 0;
		for (let i = 0; i < 4; i++) {
			total += (items[i] as HTMLElement).getBoundingClientRect().width;
		}
		const style = getComputedStyle(ul);
		const gapRaw = style.columnGap || style.gap || "6px";
		const gap = Number.parseFloat(gapRaw) || 6;
		total += gap * 3;
		setMaxPx(Math.ceil(total));
	}, [labels, scrollMode]);

	const syncFade = useCallback(() => {
		const el = scrollRef.current;
		if (!el || !scrollMode) {
			setFadeEnd(false);
			return;
		}
		setFadeEnd(el.scrollWidth > el.clientWidth + 1);
	}, [scrollMode]);

	useLayoutEffect(() => {
		if (!scrollMode) {
			setMaxPx(null);
			setFadeEnd(false);
			return;
		}
		measure();
		const id = requestAnimationFrame(() => {
			measure();
			syncFade();
		});
		return () => cancelAnimationFrame(id);
	}, [measure, scrollMode, syncFade]);

	useLayoutEffect(() => {
		if (!scrollMode) return;
		const el = scrollRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			measure();
			requestAnimationFrame(syncFade);
		});
		ro.observe(el);
		window.addEventListener("resize", measure);
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [measure, scrollMode, syncFade]);

	useLayoutEffect(() => {
		if (!scrollMode || maxPx == null) return;
		const id = requestAnimationFrame(() => syncFade());
		return () => cancelAnimationFrame(id);
	}, [maxPx, scrollMode, syncFade]);

	const onScroll = useCallback(() => {
		const el = scrollRef.current;
		if (!el || !scrollMode) return;
		const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
		setFadeEnd(!atEnd && el.scrollWidth > el.clientWidth + 1);
	}, [scrollMode]);

	const dragRef = useRef<{
		pointerId: number | null;
		startX: number;
		startScroll: number;
		nextScroll: number;
		raf: number | null;
	}>({
		pointerId: null,
		startX: 0,
		startScroll: 0,
		nextScroll: 0,
		raf: null,
	});

	const onPointerDown = useCallback(
		(e: React.PointerEvent<HTMLUListElement>) => {
			if (!scrollMode || e.button !== 0) return;
			const el = scrollRef.current;
			if (!el || el.scrollWidth <= el.clientWidth) return;
			dragRef.current = {
				pointerId: e.pointerId,
				startX: e.clientX,
				startScroll: el.scrollLeft,
				nextScroll: el.scrollLeft,
				raf: null,
			};
			el.setPointerCapture(e.pointerId);
			el.classList.add("cursor-grabbing");
		},
		[scrollMode],
	);

	const onPointerMove = useCallback((e: React.PointerEvent<HTMLUListElement>) => {
		const { pointerId, startX, startScroll } = dragRef.current;
		if (pointerId == null || e.pointerId !== pointerId) return;
		const el = scrollRef.current;
		if (!el) return;
		dragRef.current.nextScroll = startScroll - (e.clientX - startX);
		if (dragRef.current.raf == null) {
			dragRef.current.raf = requestAnimationFrame(() => {
				const currentEl = scrollRef.current;
				if (currentEl) currentEl.scrollLeft = dragRef.current.nextScroll;
				dragRef.current.raf = null;
			});
		}
		e.preventDefault();
	}, []);

	const endPointerDrag = useCallback((e: React.PointerEvent<HTMLUListElement>) => {
		const { pointerId } = dragRef.current;
		if (pointerId == null || e.pointerId !== pointerId) return;
		const el = scrollRef.current;
		try {
			el?.releasePointerCapture(pointerId);
		} catch {
			/* already released */
		}
		if (dragRef.current.raf != null) {
			cancelAnimationFrame(dragRef.current.raf);
		}
		dragRef.current = { pointerId: null, startX: 0, startScroll: 0, nextScroll: 0, raf: null };
		el?.classList.remove("cursor-grabbing");
	}, []);

	const onWheel = useCallback(
		(e: React.WheelEvent<HTMLUListElement>) => {
			if (!scrollMode) return;
			const el = scrollRef.current;
			if (!el || el.scrollWidth <= el.clientWidth) return;
			const absY = Math.abs(e.deltaY);
			const absX = Math.abs(e.deltaX);
			if (e.shiftKey || absX >= absY) {
				const delta = e.shiftKey ? e.deltaY : e.deltaX;
				if (delta === 0) return;
				el.scrollLeft += delta;
				e.preventDefault();
			}
		},
		[scrollMode],
	);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLUListElement>) => {
			if (!scrollMode) return;
			const el = scrollRef.current;
			if (!el) return;
			const step = 80;
			if (e.key === "ArrowLeft") {
				el.scrollLeft -= step;
				e.preventDefault();
			} else if (e.key === "ArrowRight") {
				el.scrollLeft += step;
				e.preventDefault();
			}
		},
		[scrollMode],
	);

	if (labels.length === 0) return null;

	if (!scrollMode) {
		return (
			<ul
				className="m-0 flex list-none flex-wrap gap-1.5 p-0 pt-1 xs:gap-2"
				aria-label="Selected sectors"
			>
				{labels.map((label, i) => (
					<li key={`${i}-${label}`}>
						<span className={heroStyleSectorPillClass}>{label}</span>
					</li>
				))}
			</ul>
		);
	}

	return (
		<div
			className="relative min-w-0 max-w-full pt-1"
			style={maxPx != null ? { maxWidth: `${maxPx}px` } : { maxWidth: "min(100%, 22rem)" }}
		>
			<ul
				ref={scrollRef}
				tabIndex={0}
				onScroll={onScroll}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={endPointerDrag}
				onPointerCancel={endPointerDrag}
				onWheel={onWheel}
				onKeyDown={onKeyDown}
				className="m-0 flex min-w-0 cursor-grab list-none flex-nowrap gap-1.5 overflow-x-auto overscroll-x-contain p-0 [-ms-overflow-style:none] [scrollbar-width:none!important] [-webkit-overflow-scrolling:touch] xs:gap-2 [&::-webkit-scrollbar]:!hidden [&::-webkit-scrollbar-thumb]:!bg-transparent [&::-webkit-scrollbar-track]:!bg-transparent touch-pan-x select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/30 active:cursor-grabbing"
				style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
				aria-label="Selected sectors — scroll horizontally for more"
			>
				{labels.map((label, i) => (
					<li key={`${i}-${label}`}>
						<span className={heroStyleSectorPillClass}>{label}</span>
					</li>
				))}
			</ul>
			{fadeEnd ? (
				<div
					className="pointer-events-none absolute inset-y-0 right-0 top-0 w-10 bg-gradient-to-l from-[var(--color-core-frosted-mist)] to-transparent dark:from-[var(--color-core-midnight)] sm:w-12"
					aria-hidden
				/>
			) : null}
		</div>
	);
}
